import clsx from "clsx";
import { X } from "lucide-react";
import { type ReactNode, useContext } from "react";
import { createPortal } from "react-dom";
import { useEscape, WindowLayer } from "./Confirm";

/*
 * A popup inside a window (RecordWindow): the window dimmed, a card over it with a title, a close
 * button and whatever it holds (e.g. a search to add items). Only that window: the page and other
 * windows stay as they are. Escape, the close button or a click on the dimmed window around it closes
 * it (Escape doesn't close the window).
 * The window provides where it goes (WindowLayer); confirmations work the same way (Confirm.tsx).
 */

export { WindowLayer };

export function WindowPopup(
    props: Readonly<{
        title: string;
        onClose: () => void;
        /** Near the window's top, not in its middle: a search whose results grow and shrink (it would jump about, centred). */
        top?: boolean;
        /** A picture (a photo full size): wider, and it doesn't scroll: what's in it shrinks to fit (min-h-0 flex-1 on the picture). */
        picture?: boolean;
        children: ReactNode;
    }>,
) {
    const layer = useContext(WindowLayer);
    // Escape closes this (wherever the focus is), not the window.
    useEscape(props.onClose);
    if (!layer) return null;
    return createPortal(
        <div
            className={clsx("confirm-shade absolute inset-0 z-30 flex justify-center bg-black/30 p-4", props.top ? "items-start pt-6" : "items-center")}
            role="dialog"
            aria-modal="true"
            aria-label={props.title}
            // A click on the dimmed window around the card closes it (nothing's lost: what it does is done as it's done).
            onPointerDown={(e) => {
                if (e.target === e.currentTarget) props.onClose();
            }}
        >
            <div
                className={clsx(
                    "confirm-card flex max-h-full flex-col overflow-hidden rounded-xl border border-line bg-surface text-sm text-ink shadow-2xl",
                    props.picture ? "w-[min(56rem,100%)]" : "w-[min(34rem,100%)]",
                )}
            >
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-4 py-2.5">
                    <h2 className="text-[15px] font-semibold">{props.title}</h2>
                    <button type="button" className="btn-icon" aria-label="Close" title="Close" onClick={props.onClose}>
                        <X className="size-4" />
                    </button>
                </div>
                <div className={clsx("min-h-0 p-3", props.picture ? "flex flex-col" : "overflow-y-auto")}>{props.children}</div>
            </div>
        </div>,
        layer,
    );
}
